<template>
  <div class="break-crumb">
    <a-breadcrumb separator=">" class="break-crumb__nav">
      <a-breadcrumb-item
        v-for="(item, index) in breadcrumbs"
        :key="index"
      >
        <router-link
          v-if="item.key && index < breadcrumbs.length - 1"
          :to="item.key"
          class="break-crumb__link"
        >
          {{ labelOf(item) }}
        </router-link>
        <span
          v-else
          class="break-crumb__current"
          :class="{ 'break-crumb__current--last': index === breadcrumbs.length - 1 }"
        >
          {{ labelOf(item) }}
        </span>
      </a-breadcrumb-item>
    </a-breadcrumb>

    <h1 v-if="pageTitle" class="break-crumb__title">{{ pageTitle }}</h1>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

type BreadcrumbItem = {
  key?: string;
  value: string;
};

const route = useRoute();
const { t, te } = useI18n();

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const list = (route.meta?.breadcrumb as BreadcrumbItem[]) || [];
  return Array.isArray(list) ? list : [];
});

const labelOf = (item: BreadcrumbItem) => {
  const key = item?.value?.trim() || "";
  if (!key) return "";
  return te(key) ? t(key) : key;
};

const pageTitle = computed(() => {
  const last = breadcrumbs.value[breadcrumbs.value.length - 1];
  if (last) return labelOf(last);
  const name = route.meta?.title || route.name;
  if (typeof name === "string" && te(name)) return t(name);
  return typeof name === "string" ? name : "";
});
</script>

<style scoped>
.break-crumb {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 0;
  background: transparent;
}

.break-crumb__nav {
  margin: 0;
  line-height: 1.2;
}

.break-crumb :deep(.ant-breadcrumb) {
  font-size: 13px;
  line-height: 1.3;
}

.break-crumb :deep(.ant-breadcrumb-separator) {
  margin: 0 8px;
  color: #9ca3af;
}

.break-crumb__link {
  color: #9ca3af !important;
  font-weight: 400;
  transition: color 0.15s ease;
}

.break-crumb__link:hover {
  color: var(--primary-dark, #102542) !important;
}

.break-crumb__current {
  color: #9ca3af;
  font-weight: 400;
}

.break-crumb__current--last {
  color: #111827;
}

.break-crumb__title {
  margin: 0;
  padding: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: #111827;
}

@media (max-width: 767px) {
  .break-crumb__title {
    font-size: 22px;
  }
}
</style>

<style>
.dark-mode .break-crumb a,
.dark-mode .break-crumb span,
.dark-mode .break-crumb .ant-breadcrumb-separator,
.dark-mode .break-crumb .break-crumb__title {
  color: #fff !important;
}

.dark-mode .break-crumb .break-crumb__link,
.dark-mode .break-crumb .break-crumb__current {
  color: rgba(255, 255, 255, 0.65) !important;
}

.dark-mode .break-crumb .break-crumb__current--last {
  color: #fff !important;
}
</style>
